import axios from '../../../axiosConfig';

export class EventosService {
    // Endpoints específicos para cada tipo de evento
    static ENDPOINTS = {
        LLEGADA: '/enviarLlegada',
        ENTRADA: '/enviarEntrada',
        SALIDA: '/enviarSalida'
    };

    // Obtiene la geolocalización actual del dispositivo
    static async obtenerGeolocalizacion() {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                reject(new Error('La geolocalización no es compatible con este navegador.'));
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    
                    // Validar coordenadas
                    if (this.validarCoordenadas(latitude, longitude)) {
                        resolve({
                            latitud: latitude,
                            longitud: longitude,
                            timestamp: new Date().toISOString()
                        });
                    } else {
                        reject(new Error('Coordenadas GPS inválidas'));
                    }
                },
                () => {
                    reject(new Error('No se pudo obtener la geolocalización.'));
                },
                {
                    enableHighAccuracy: true,
                    timeout: 15000,
                    maximumAge: 60000
                }
            );
        });
    };

    static validarCoordenadas(lat, lon) {
        return !isNaN(lat) && !isNaN(lon) && 
               lat >= -90 && lat <= 90 && 
               lon >= -180 && lon <= 180 &&
               lat !== 0 && lon !== 0;
    };

    static normalizarCoordenadas(coords) {
        if (!coords) {
            throw new Error('Coordenadas no proporcionadas');
        }

        // Intentar diferentes formatos de coordenadas
        const latitud = coords.latitud || coords.lat || coords.latitude;
        const longitud = coords.longitud || coords.lon || coords.lng || coords.longitude;

        if (!latitud || !longitud) {
            console.error('Coordenadas recibidas:', coords);
            throw new Error('Formato de coordenadas inválido. Se requiere latitud y longitud');
        }

        // Validar que sean números válidos
        if (!this.validarCoordenadas(latitud, longitud)) {
            throw new Error(`Coordenadas fuera de rango: lat=${latitud}, lon=${longitud}`);
        }

        return {
            latitud: Number(latitud),
            longitud: Number(longitud)
        };
    };

    // Obtiene los códigos DANE desde las remesas de operacionData
    static obtenerCodigosDane(operacionData, fase) {
        if (!operacionData?.remesas || operacionData.remesas.length === 0) {
            return { origen_dane: null, destino_dane: null };
        }

        // Tomar la primera remesa como referencia (todas deberían tener la misma ruta)
        // To improve: validar que todas las remesas tengan la misma ruta
        const remesa = operacionData.remesas[0];

        const resultado = {
            origen_dane: remesa.origen_dane || null,
            destino_dane: remesa.destino_dane || null
        };

        // Para eventos de CARGUE, destino_dane debe ser null
        // Para eventos de DESCARGUE, destino_dane se mantiene
        if (fase === 'CARGUE') {
            resultado.destino_dane = null;
        } else if (fase === 'DESCARGUE') {
            resultado.origen_dane = null;
        }

        return resultado;
    };

    // Prepara datos base para enviar un evento
    static prepararDatosEvento({
        manifiesto, 
        evento, 
        fase,
        observaciones,
        coords,
        evidencias = [],
        remesa = null,
        operacionData = null
    }) {
        const tokenConductor = localStorage.getItem('tokenOperacion');
        if (!tokenConductor) {
            throw new Error('Token de operación no encontrado');
        }

        // Normalizar coordenadas
        const coordenadasNormalizadas = this.normalizarCoordenadas(coords);

        const formData = new FormData();

        // Datos obligatorios
        formData.append('manifiesto', manifiesto);
        formData.append('tokenConductor', tokenConductor);
        formData.append('observaciones', observaciones || `${evento} ${fase} reportado desde OnRoad`);
        formData.append('latitud', coordenadasNormalizadas.latitud.toString());
        formData.append('longitud', coordenadasNormalizadas.longitud.toString());

        // Remesa específica (opcional)
        if (remesa && typeof remesa === 'number') {
            formData.append('remesa', remesa);
        }

        // Obtener códigos DANE dinámicamente desde operacionData
        const codigosDane = this.obtenerCodigosDane(operacionData, fase);

        if (codigosDane.origen_dane) {
            formData.append('origen_dane', codigosDane.origen_dane);
        }

        if (codigosDane.destino_dane) {
            formData.append('destino_dane', codigosDane.destino_dane);
        }

        // Flags de fase
        if (fase === 'CARGUE') {
            formData.append('cargue', 1);
        } else if (fase === 'DESCARGUE') {
            formData.append('descargue', 1);
        }

        // Evidencias fotográficas
        if (evidencias && evidencias.length > 0) {
            evidencias.forEach((evidencia) => {
                if (evidencia instanceof File) {
                    formData.append('novedadImagen', evidencia);
                }
            });
        }

        return formData;
    };

    // Reporta un evento específico (LLEGADA, ENTRADA, SALIDA)
    static async reportarEvento({
        manifiesto,
        evento,
        fase,
        observaciones = '',
        coords = null,
        evidencias = [],
        remesa = null,
        operacionData = null
    }) {
        try {
            // Validar parámetros obligatorios
            if (!manifiesto || !evento || !fase) {
                throw new Error('Parámetros obligatorios faltantes: manifiesto, evento, fase');
            }

            // Validar que el evento sea válido
            if (!this.ENDPOINTS[evento]) {
                throw new Error(`Evento no válido: ${evento}`);
            }

            // Obtener coordenadas si no se proporcionaron
            const coordenadas = coords || await this.obtenerGeolocalizacion();

            // Preparar datos del evento
            const formData = this.prepararDatosEvento({
                manifiesto,
                evento,
                fase,
                observaciones,
                coords: coordenadas,
                evidencias,
                remesa,
                operacionData
            });

            // Enviar al endpoint correspondiente
            const endpoint = this.ENDPOINTS[evento];
            const response = await axios.post(endpoint, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            // Validar respuesta
            if (response.data?.success) {
                return {
                    success: true,
                    message: response.data.message || `${evento} ${fase} reportado exitosamente`,
                    data: response.data || response.data.data
                };
            } else {
                return {
                    success: false,
                    message: response.data?.message || `Error al reportar ${evento} ${fase}`,
                    errors: response.data?.errors || null
                };
            }

        } catch (error) {
            console.error(`Error reportando ${evento} ${fase}:`, error);
            const backendMessage = error.response?.data?.message;
            const backendErrors = error.response?.data?.errors;
            return {
                success: false,
                message: backendMessage || error.message || 'Error de conexión al reportar evento',
                errors: backendErrors || null,
                statusCode: error.response?.status
            };
        }
    };

    // ====================================
    // AUTENTICACIÓN Y VALIDACIÓN
    // ====================================
    
    static async validarConductor({ identificacion, manifiesto, tenantEmpresa }) {
        try {
            const response = await axios.get('/validarConductor', {
                params: { identificacion, manifiesto, tenantEmpresa },
                validateStatus: (status) => status >= 200 && status < 500
            });

            if (response.data?.tokenOperacion) {
                return {
                    success: true,
                    data: response.data,
                    operacion: response.data.operacion
                };
            } else {
                return {
                    success: false,
                    message: response.data?.message || 'Credenciales inválidas',
                    errors: response.data?.errors
                };
            }
        } catch (error) {
            return {
                success: false,
                message: error.message || 'Error de conexión',
                error
            };
        }
    }

    static async revalidarManifiesto() {
        const identificacion = localStorage.getItem('idConductor');
        const manifiesto = localStorage.getItem('manifiesto');
        const tenantEmpresa = localStorage.getItem('tenantEmpresa');

        if (!identificacion || !manifiesto || !tenantEmpresa) {
            return { success: false, message: 'Datos de sesión incompletos' };
        }

        return await this.validarConductor({ identificacion, manifiesto, tenantEmpresa });
    }

}